<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text-fill';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import ImageCell from '@js/components/tables/cells/ImageCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'image',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Image',
                    width: 0,
                }),
            cell: (props) =>
                h(ImageCell, {
                    src: props.row.original.image,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.display({
            id: 'description',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Description',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    props.row.original.description
                        ? {
                              default: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Description',
                                          content:
                                              props.row.original.description,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir la description',
                                                  type: 'secondary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  ),
                          }
                        : {},
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.ai.productImageAttachment',
                                    {
                                        tenant: tenant.id,
                                        aiProductImageAttachment:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.ai.postProductImageAttachments', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.ai.productImageAttachment', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
